import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Phone, Plane, Check } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SearchWidget from "@/components/shared/SearchWidget";
import ProviderStrip from "@/components/shared/ProviderStrip";
import PriceBadge from "@/components/shared/PriceBadge";
import CategoryCard from "@/components/shared/CategoryCard";
import FAQAccordion from "@/components/shared/FAQAccordion";
import { findCity, findCategory, BRAND, FAQS_GENERAL } from "@/lib/data";

export default function CityDetail() {
  const { slug } = useParams();
  const city = findCity(slug);
  if (!city) return <Navigate to="/cities" replace />;

  const cats = city.categories.map(findCategory).filter(Boolean);
  const cityFAQs = [
    {
      q: `Which airports does Carztohire serve in ${city.name}?`,
      a: `We compare rentals at ${city.airports.join(", ")} across every major provider. Downtown and neighborhood pickup locations are available on request.`,
    },
    {
      q: `What car categories are most popular in ${city.name}?`,
      a: `${city.categories.map((s) => findCategory(s)?.name).filter(Boolean).join(", ")} are the most-requested classes. Availability shifts by season — call us for a live comparison.`,
    },
    ...FAQS_GENERAL.slice(0, 3),
  ];

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative bg-[#0B2545] text-white pt-14 md:pt-20 pb-14 md:pb-20 overflow-hidden" data-testid="city-hero">
        <div className="absolute inset-0 pointer-events-none opacity-30">
          <img src={city.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/60 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline-light mb-6">
            <Link to="/cities" className="text-white/70 hover:text-white transition-colors">Cities</Link>
            <span className="mx-2 text-white/40">/</span>
            <span className="text-white">{city.name}</span>
          </div>

          <h1 className="font-serif text-[12vw] md:text-[7vw] lg:text-[5.5vw] leading-[1] tracking-[-0.02em] text-white max-w-4xl">
            {city.name} car rentals — compared.
          </h1>
          <p className="mt-6 text-white/85 text-lg md:text-xl max-w-3xl leading-relaxed">
            {city.tagline}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <PriceBadge price={city.startingFrom} size="lg" tone="dark" />
            {city.airports.map((a) => (
              <div
                key={a}
                className="inline-flex items-center gap-2 border border-white/25 px-3 py-1.5 font-mono text-xs tracking-widest uppercase text-white/85"
              >
                <Plane className="h-3.5 w-3.5" strokeWidth={1.8} /> {a}
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a href={BRAND.phoneHref} className="btn-inverse" data-testid="city-call">
              <Phone className="h-4 w-4" strokeWidth={2} /> Call {BRAND.phone}
            </a>
            <Link to={`/contact?pickup_city=${encodeURIComponent(city.name)}`} className="btn-ghost-dark" data-testid="city-quote">
              Get a free quote <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>

        <div className="relative -mb-14 md:-mb-16 z-10 mt-12 mx-auto max-w-[1600px] px-5 md:px-10">
          <SearchWidget variant="compact" defaultCity={city.name} />
        </div>
      </section>

      <div className="h-14 md:h-16 bg-white" />
      <ProviderStrip />

      {/* About the market */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline mb-4">The market</div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0B2545] leading-tight">
              Why compare rentals in {city.name}.
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-[#5B6472] text-lg leading-relaxed max-w-2xl mb-8">
              {city.description}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                `${city.airports.length} airport${city.airports.length > 1 ? "s" : ""} served`,
                "Every major US provider compared",
                "Downtown + neighborhood pickups",
                "Live availability by season",
              ].map((p) => (
                <div key={p} className="flex items-center gap-3 text-[#0B2545]">
                  <Check className="h-4 w-4" strokeWidth={2} />
                  <span className="text-sm">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 md:py-28 bg-[#F5F7FA] border-y border-line">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline mb-4">Categories in {city.name}</div>
          <h2 className="font-serif text-3xl md:text-5xl text-[#0B2545] leading-tight mb-12 max-w-3xl">
            Most-rented car classes in {city.name}.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {cats.map((c, i) => (
              <CategoryCard key={c.slug} category={c} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-4">{city.name} FAQ</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#0B2545]">
              City-specific questions.
            </h2>
          </div>
          <div className="md:col-span-8">
            <FAQAccordion items={cityFAQs} testIdPrefix={`city-${city.slug}-faq`} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0B2545] text-white">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <h2 className="font-serif text-4xl md:text-5xl leading-tight text-white max-w-3xl">
            Renting in {city.name}? Call us or request a quote — we'll compare providers for you.
          </h2>
          <div className="mt-10 flex flex-col md:flex-row gap-3">
            <a href={BRAND.phoneHref} className="btn-inverse">
              <Phone className="h-4 w-4" strokeWidth={2} /> {BRAND.phone}
            </a>
            <Link to={`/contact?pickup_city=${encodeURIComponent(city.name)}`} className="btn-ghost-dark">
              Get a free quote <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>
          <p className="mt-8 text-xs text-white/55 max-w-2xl leading-relaxed">
            Pricing shown is estimated starting-from pricing based on recent averages across
            providers in {city.name}. Live availability and final pricing are confirmed by our team.
          </p>
        </div>
      </section>
    </PageShell>
  );
}
